import { useState } from 'react';
import { cn } from '../lib/utils';

export interface FacetValue {
  facetKey: string;
  operator: string;
  facetValue: string;
}

interface FacetEditorProps {
  facets: FacetValue[];
  onChange: (facets: FacetValue[]) => void;
  suggestedKeys?: string[];
  compact?: boolean;
}

const OPERATORS = ['EQ', 'GTE', 'LTE', 'EXISTS', 'NOT_EQ'];
const DEFAULT_KEYS = ['event', 'activity', 'streak', 'grade_level', 'response_ms', 'correct', 'word_text', 'active_ms'];

export default function FacetEditor({ facets, onChange, suggestedKeys, compact }: FacetEditorProps) {
  const keys = suggestedKeys || DEFAULT_KEYS;

  const addFacet = () => {
    onChange([...facets, { facetKey: '', operator: 'EQ', facetValue: '' }]);
  };

  const removeFacet = (index: number) => {
    onChange(facets.filter((_, i) => i !== index));
  };

  const updateFacet = (index: number, field: keyof FacetValue, value: string) => {
    const updated = facets.map((f, i) =>
      i === index ? { ...f, [field]: value } : f
    );
    onChange(updated);
  };

  return (
    <div className="space-y-2">
      {facets.map((facet, i) => (
        <div key={i} className={cn("flex gap-2 items-center", compact && "gap-1")}>
          <div className="relative flex-1">
            <input
              type="text"
              value={facet.facetKey}
              onChange={(e) => updateFacet(i, 'facetKey', e.target.value)}
              placeholder="key"
              list="facet-keys"
              className={cn(
                "w-full rounded border border-gray-300 px-2 py-1 text-sm",
                compact && "text-xs"
              )}
            />
          </div>
          <select
            value={facet.operator}
            onChange={(e) => updateFacet(i, 'operator', e.target.value)}
            className={cn(
              "rounded border border-gray-300 px-2 py-1 text-sm bg-white",
              compact && "text-xs"
            )}
          >
            {OPERATORS.map(op => (
              <option key={op} value={op}>{op}</option>
            ))}
          </select>
          {facet.operator !== 'EXISTS' && (
            <input
              type="text"
              value={facet.facetValue}
              onChange={(e) => updateFacet(i, 'facetValue', e.target.value)}
              placeholder="value"
              className={cn(
                "flex-1 rounded border border-gray-300 px-2 py-1 text-sm",
                compact && "text-xs"
              )}
            />
          )}
          <button
            onClick={() => removeFacet(i)}
            className="text-red-500 hover:text-red-700 text-sm px-1"
          >
            &times;
          </button>
        </div>
      ))}
      <datalist id="facet-keys">
        {keys.map(k => <option key={k} value={k} />)}
      </datalist>
      <button
        onClick={addFacet}
        className="text-sm text-blue-600 hover:text-blue-800"
      >
        + Add facet
      </button>
    </div>
  );
}

export function FacetPills({ facets }: { facets: FacetValue[] }) {
  if (!facets || facets.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-1">
      {facets.map((f, i) => (
        <span
          key={i}
          className="inline-flex items-center gap-0.5 rounded-full bg-blue-100 px-2 py-0.5 text-xs text-blue-800"
        >
          {f.facetKey}
          <span className="text-blue-500">{f.operator}</span>
          {f.operator !== 'EXISTS' && f.facetValue}
        </span>
      ))}
    </div>
  );
}
